/**
 * Feedback.jsx — full-page feedback form.
 *
 * Comprehensive version of the floating widget:
 *   - 1-5 star rating (required)
 *   - Category (Bug, Feature request, Pricing, Praise, Other) — required
 *   - Free-text message — required
 *   - Optional screenshot URL (paste link) — keeps the form lightweight
 *     without forcing us to handle binary uploads
 *
 * Linked from the sidebar nav. The floating widget hides on this page since
 * the full form is already on-screen.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Loader2, Check, MessageSquareHeart, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORIES = [
    { value: 'Bug', label: '🐞 Bug or something broken' },
    { value: 'Feature request', label: '✨ Feature request' },
    { value: 'Pricing', label: '💰 Pricing or billing' },
    { value: 'Praise', label: '🙌 Praise or what\'s working' },
    { value: 'Other', label: '💬 Other / general' },
];

function _ratingLabel(n) {
    if (n <= 1) return 'Not great';
    if (n === 2) return 'Could be better';
    if (n === 3) return "It's okay";
    if (n === 4) return 'Pretty good';
    return 'Love it';
}

export default function FeedbackPage() {
    const navigate = useNavigate();
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [category, setCategory] = useState('');
    const [message, setMessage] = useState('');
    const [screenshotUrl, setScreenshotUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submittedId, setSubmittedId] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/feedback/my-recent?limit=5');
                if (!cancelled) setRecent(r.data?.rows || []);
            } catch (_e) {
                if (!cancelled) setRecent([]);
            } finally {
                if (!cancelled) setLoadingRecent(false);
            }
        })();
        return () => { cancelled = true; };
    }, [submittedId]);

    const displayRating = hoverRating || rating;

    const validate = () => {
        if (!rating) {
            toast.error('Pick a star rating so we can prioritize.');
            return false;
        }
        if (!category) {
            toast.error('Pick a category.');
            return false;
        }
        const trimmed = message.trim();
        if (trimmed.length < 5) {
            toast.error('Add a bit more detail — even one sentence helps.');
            return false;
        }
        return true;
    };

    const submit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        try {
            // If a screenshot URL was provided, append it to the message body
            // (the backend has a single text field; this keeps things simple).
            const trimmedUrl = screenshotUrl.trim();
            const fullMessage = trimmedUrl
                ? `${message.trim()}\n\nScreenshot: ${trimmedUrl}`
                : message.trim();
            const r = await api.post('/feedback', {
                rating,
                message: fullMessage,
                category,
                surface: 'page',
                page_url: window.location.pathname,
            });
            setSubmittedId(r.data?.id || 'ok');
            toast.success('Thanks — we got it.');
            // Reset for a follow-up submission if needed
            setRating(0);
            setCategory('');
            setMessage('');
            setScreenshotUrl('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Couldn\'t send right now — please try again in a moment.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageHeader
                title="Share feedback"
                subtitle="Tell us what's working, what's broken, or what's missing. Every submission lands in front of the founding team."
                testId="feedback-page-header"
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Main form */}
                <Card className="lg:col-span-2" data-testid="feedback-page-card">
                    <CardHeader>
                        <CardTitle className="font-display flex items-center gap-2">
                            <MessageSquareHeart className="h-4 w-4 text-amber-600" />
                            How was your experience?
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        {/* Rating */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 block">
                                Rating <span className="text-red-500">*</span>
                            </Label>
                            <div className="flex items-center gap-1.5" data-testid="feedback-page-rating">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onMouseEnter={() => setHoverRating(n)}
                                        onMouseLeave={() => setHoverRating(0)}
                                        onClick={() => setRating(n)}
                                        aria-label={`${n} out of 5 stars`}
                                        data-testid={`feedback-page-star-${n}`}
                                        className="p-1 rounded transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                                    >
                                        <Star
                                            className={`h-9 w-9 ${
                                                n <= displayRating
                                                    ? 'text-amber-500 fill-amber-500'
                                                    : 'text-slate-300'
                                            }`}
                                            strokeWidth={1.5}
                                        />
                                    </button>
                                ))}
                                {displayRating > 0 && (
                                    <span className="ml-3 text-sm font-medium text-slate-700" data-testid="feedback-page-rating-label">
                                        {displayRating}/5 · {_ratingLabel(displayRating)}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Category */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 block">
                                Category <span className="text-red-500">*</span>
                            </Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger data-testid="feedback-page-category">
                                    <SelectValue placeholder="Pick what fits best" />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c.value} value={c.value} data-testid={`feedback-page-category-${c.value}`}>
                                            {c.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Message */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 block" htmlFor="feedback-page-message">
                                Your thoughts <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                                id="feedback-page-message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={7}
                                maxLength={5000}
                                placeholder="What worked well, what didn't, what would you change? Be as specific as you can — examples help us act on it fast."
                                data-testid="feedback-page-message"
                                className="resize-none"
                            />
                            <div className="mt-1 text-[11px] text-slate-400 text-right">{message.length}/5000</div>
                        </div>

                        {/* Optional screenshot URL */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 block" htmlFor="feedback-page-screenshot">
                                Screenshot URL <span className="text-slate-400">(optional)</span>
                            </Label>
                            <Input
                                id="feedback-page-screenshot"
                                value={screenshotUrl}
                                onChange={(e) => setScreenshotUrl(e.target.value)}
                                placeholder="Paste a link to a screenshot (Imgur, Dropbox, Google Drive…)"
                                data-testid="feedback-page-screenshot"
                            />
                            <p className="mt-1 text-[11px] text-slate-500">Screenshots help us reproduce bugs faster.</p>
                        </div>

                        {/* Actions */}
                        <div className="pt-2 flex items-center justify-between border-t border-slate-100">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/app/dashboard')}
                                data-testid="feedback-page-cancel"
                            >
                                Back to dashboard
                            </Button>
                            <Button
                                onClick={submit}
                                disabled={submitting || !rating || !category || message.trim().length < 5}
                                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
                                data-testid="feedback-page-submit"
                            >
                                {submitting ? (
                                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending…</>
                                ) : (
                                    <><MessageSquareHeart className="h-4 w-4 mr-2" /> Send feedback</>
                                )}
                            </Button>
                        </div>

                        {submittedId && (
                            <div
                                className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2.5"
                                data-testid="feedback-page-success-banner"
                            >
                                <Check className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-emerald-900 leading-snug">
                                    <b>Thanks — got it.</b> We read every note. You may hear back at your account email if we have a follow-up question.
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Right column: recent feedback + tips */}
                <div className="space-y-4">
                    <Card data-testid="feedback-page-recent-card">
                        <CardHeader>
                            <CardTitle className="font-display text-base flex items-center gap-2">
                                <History className="h-4 w-4 text-slate-700" />
                                Your recent feedback
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loadingRecent ? (
                                <div className="text-xs text-slate-500 flex items-center gap-2">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                                </div>
                            ) : recent.length === 0 ? (
                                <p className="text-xs text-slate-500" data-testid="feedback-page-recent-empty">
                                    You haven&apos;t sent feedback yet — fire away and it&apos;ll show up here.
                                </p>
                            ) : (
                                <ul className="space-y-3" data-testid="feedback-page-recent-list">
                                    {recent.map((r) => (
                                        <li key={r.id} className="border-l-2 border-amber-300 pl-3" data-testid={`feedback-page-recent-${r.id}`}>
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <div className="flex">
                                                    {[1, 2, 3, 4, 5].map((n) => (
                                                        <Star
                                                            key={n}
                                                            className={`h-3 w-3 ${n <= r.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`}
                                                            strokeWidth={1.5}
                                                        />
                                                    ))}
                                                </div>
                                                {r.category && (
                                                    <Badge variant="outline" className="text-[10px] font-normal h-4 px-1.5">
                                                        {r.category}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-700 line-clamp-2">{r.message}</p>
                                            <p className="text-[10px] text-slate-400 mt-0.5">
                                                {new Date(r.created_at).toLocaleString()}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="font-display text-base">Tips for great feedback</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-2 text-xs text-slate-600">
                                <li className="flex items-start gap-2">
                                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                                    <span><b>Be specific.</b> &ldquo;The Lead Finder map flickers on Safari iOS 17&rdquo; is much more actionable than &ldquo;the map is broken.&rdquo;</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                                    <span><b>Tell us what you were trying to do.</b> Helps us understand whether to fix a bug or add a feature.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                                    <span><b>One topic per submission.</b> Easier for us to triage and reply.</span>
                                </li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
}
